import { countries as allCountries } from '@/helpers/countries'
import type { Currency } from '@caterdesk/utils--money'
import { findCountryByLocale } from '@fusion/locale/countries'

export const DEFAULT_CURRENCY = 'GBP' as const

/**
 * Get a common price formatter for the specific locale.
 *
 * @param locale The locale we want to format the price in.
 *
 * @returns The Intl.NumberFormat instance to format a price.
 */
export function getPriceFormatter(locale: string): Intl.NumberFormat {
  let currency: Currency | string = DEFAULT_CURRENCY

  if (locale) {
    const country = findCountryByLocale(allCountries, locale)
    currency = country?.currency || DEFAULT_CURRENCY
  }

  const formatter = new Intl.NumberFormat(locale || 'en-GB', {
    style: 'currency',
    currency,
    currencySign: 'standard',
    currencyDisplay: 'narrowSymbol',
    trailingZeroDisplay: 'auto',
    maximumFractionDigits: 2,
  })

  return formatter
}

/**
 * Formats a monetary amount as currency for the given locale.
 * Uses the locale's corresponding country currency (e.g., en-GB -> GBP, de-DE -> EUR).
 *
 * @param amount - The amount to format
 * @param locale - The locale string (e.g., 'en-GB', 'de-DE')
 * @param options - Optional formatting options
 * @returns Formatted currency string (e.g., '£1', '1 €')
 */
export function formatCurrency(
  amount: number,
  locale: string,
  options?: Partial<Intl.NumberFormatOptions>,
): string {
  let currency: Currency | string = DEFAULT_CURRENCY

  if (locale) {
    const country = findCountryByLocale(allCountries, locale)
    currency = country?.currency || DEFAULT_CURRENCY
  }

  try {
    const formatter = new Intl.NumberFormat(locale || 'en-GB', {
      style: 'currency',
      currency,
      currencySign: 'standard',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options,
    })

    return formatter.format(amount)
  } catch (error) {
    // Fallback if locale or currency is not supported
    console.warn(
      `Currency formatting failed for locale "${locale}" and currency "${currency}"`,
      error,
    )
    return `${amount} ${currency}`
  }
}
